import React, { useEffect, useState } from 'react';
import { api } from './api.js';
import { useLang } from './i18n.js';
import { thb, shortDate, relTime } from './money.js';
import { Overlay, Spinner, Empty, Avatar, EntityGlyph, Facts, Tier, entityColor } from './ui.jsx';

/* ------------------------------------------------------------------ shared */

function Head({ children, onClose }) {
  return (
    <div className="drawer-head">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        <button className="icon-btn" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
    </div>
  );
}

function Tabs({ tabs, value, onChange }) {
  return (
    <div className="drawer-tabs">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={`drawer-tab${tab.id === value ? ' is-on' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function Timeline({ items, emptyLabel }) {
  const { lang } = useLang();
  if (!items?.length) return <Empty>{emptyLabel}</Empty>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {items.map((a) => (
        <div key={a.id} style={{ display: 'flex', gap: 10 }}>
          <Avatar initials={a.user_initials} seed={a.user_var} size={26} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {a.subject || a.type}
              <span
                style={{ fontWeight: 400, color: 'var(--ink-3)', marginLeft: 6, fontSize: 11.5 }}
              >
                {a.user_name} · {relTime(a.created_at, lang)}
              </span>
            </div>
            {a.body && (
              <div
                style={{
                  fontSize: 12.5,
                  color: 'var(--ink-2)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {a.body}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- deal drawer */

export function DealDrawer({ id, onClose, onOpenClient, onLog, toast }) {
  const { t, lang } = useLang();
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setData(null);
    api
      .deal(id)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [id]);

  const d = data?.deal;

  return (
    <Overlay onClose={onClose}>
      {error && <div style={{ padding: 24 }}>{error}</div>}
      {!error && !d && <Spinner label={t('loading')} />}
      {d && (
        <>
          <Head onClose={onClose}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 11,
                fontWeight: 700,
                color: entityColor(d.entity_tag),
              }}
            >
              <EntityGlyph entity={d.entity_tag} />
              {d.code}
              <span className="pill">{d.stage}</span>
              {d.idle_days > 0 && (
                <span style={{ color: 'var(--ink-3)', fontWeight: 600 }}>
                  {t('idle', { n: d.idle_days })}
                </span>
              )}
            </div>
            <div className="display" style={{ fontSize: 20, marginTop: 6, lineHeight: 1.25 }}>
              {d.title}
            </div>
            <div
              style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2, cursor: 'pointer' }}
              onClick={() => onOpenClient(d.company_id)}
            >
              {d.company_name} →
            </div>
            <div
              style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 12 }}
            >
              <span className="display num" style={{ fontSize: 26 }}>
                {thb(d.amount_satang)}
              </span>
              <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                {t('close')} {shortDate(d.close_date, lang)}
              </span>
            </div>
          </Head>

          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[
              { id: 'overview', label: t('overview') },
              { id: 'activity', label: `${t('activity')} · ${data.activities.length}` },
              { id: 'quotes', label: `${t('quotes')} · ${data.quotes.length}` },
            ]}
          />

          <div className="drawer-body">
            {tab === 'overview' && (
              <Facts
                rows={[
                  [t('owner'), d.owner_name],
                  [t('aeOwner'), d.ae_name],
                  [t('contact'), d.contact_name],
                  [t('stage'), d.stage],
                  [t('source'), d.source],
                  [t('tier'), d.tier],
                  [
                    t('weighted'),
                    `${thb(d.weighted_satang)} (${d.probability ?? 0}%)`,
                  ],
                  [t('lastActivity'), relTime(d.last_activity_at, lang)],
                ]}
              />
            )}
            {tab === 'activity' && (
              <Timeline items={data.activities} emptyLabel={t('noActivity')} />
            )}
            {tab === 'quotes' && (
              /* Populated by the FlowAccount sync in B5. Until then this is
                 honestly empty rather than absent. */
              <Empty>{t('noQuotes')}</Empty>
            )}
          </div>

          <div className="drawer-foot">
            <button
              className="btn btn-primary"
              style={{ flex: 1 }}
              onClick={() => onLog({ deal_id: d.id, label: d.title })}
            >
              {t('logActivity')}
            </button>
          </div>
        </>
      )}
    </Overlay>
  );
}

/* ------------------------------------------------------------ client drawer */

export function ClientDrawer({ id, onClose, onOpenDeal, onLog }) {
  const { t, lang } = useLang();
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setData(null);
    api
      .company(id)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [id]);

  const c = data?.company;

  return (
    <Overlay onClose={onClose}>
      {error && <div style={{ padding: 24 }}>{error}</div>}
      {!error && !c && <Spinner label={t('loading')} />}
      {c && (
        <>
          <Head onClose={onClose}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 11,
                fontWeight: 700,
                color: entityColor(c.entity_tag),
              }}
            >
              <EntityGlyph entity={c.entity_tag} />
              {c.code}
              <Tier tier={c.tier} />
              <span className="pill">{c.lifecycle}</span>
            </div>
            <div className="display" style={{ fontSize: 20, marginTop: 6 }}>
              {c.name}
            </div>
            {c.name_registered_th && (
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }} lang="th">
                {c.name_registered_th}
              </div>
            )}
            <div style={{ display: 'flex', gap: 18, marginTop: 12 }}>
              <Stat label={t('openDeals')} value={c.open_deals} />
              <Stat label={t('revenueYtd')} value={thb(c.revenue_ytd_satang)} />
            </div>
          </Head>

          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[
              { id: 'overview', label: t('overview') },
              { id: 'contacts', label: `${t('contacts')} · ${data.contacts.length}` },
              { id: 'deals', label: `${t('deal')} · ${data.deals.length}` },
              { id: 'activity', label: t('activity') },
            ]}
          />

          <div className="drawer-body">
            {tab === 'overview' && (
              <Facts
                rows={[
                  ['Sales', c.sales_owner_name],
                  ['AE', c.ae_owner_name],
                  [t('industry'), c.industry],
                  [t('tier'), c.tier],
                  [t('source'), c.source],
                  ['Tax ID', c.tax_id],
                  [
                    t('website'),
                    c.website ? (
                      <a href={c.website} target="_blank" rel="noreferrer">
                        {c.website}
                      </a>
                    ) : null,
                  ],
                  [t('lastActivity'), relTime(c.last_activity_at, lang)],
                ]}
              />
            )}

            {tab === 'contacts' &&
              (data.contacts.length === 0 ? (
                <Empty>{t('noContacts')}</Empty>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {data.contacts.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        border: '1px solid var(--line)',
                        borderRadius: 'var(--r-md)',
                        padding: 12,
                        background: p.is_primary ? 'var(--primary-soft)' : 'var(--surface)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 13.5 }}>{p.name}</span>
                        {p.is_primary === 1 && <span className="pill">{t('contact')}</span>}
                        {p.power && p.power !== 'unknown' && (
                          <span className="pill">{p.power.replace('_', ' ')}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2 }}>
                        {p.title}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 6 }}>
                        {[p.email, p.phone, p.line_id && `LINE ${p.line_id}`]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

            {tab === 'deals' &&
              (data.deals.length === 0 ? (
                <Empty>{t('noDeals')}</Empty>
              ) : (
                data.deals.map((d) => (
                  <div
                    className="row row-click"
                    key={d.id}
                    onClick={() => onOpenDeal(d.id)}
                    style={{ paddingLeft: 0, paddingRight: 0 }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="row-title" style={{ display: 'block' }}>
                        {d.title}
                      </span>
                      <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                        {d.code} · {d.stage}
                      </span>
                    </span>
                    <span className="num" style={{ fontWeight: 700 }}>
                      {thb(d.amount_satang)}
                    </span>
                  </div>
                ))
              ))}

            {tab === 'activity' && (
              <Timeline items={data.activities} emptyLabel={t('noActivity')} />
            )}
          </div>

          <div className="drawer-foot">
            <button
              className="btn btn-primary"
              style={{ flex: 1 }}
              onClick={() => onLog({ company_id: c.id, label: c.name })}
            >
              {t('logActivity')}
            </button>
          </div>
        </>
      )}
    </Overlay>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 600 }}>{label}</div>
    <div className="num" style={{ fontSize: 15, fontWeight: 700 }}>
      {value ?? '—'}
    </div>
  </div>
);
